import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/client'
import { getAccessToken } from '@/api/client'
import type { PaginatedResponse } from '@/api/client'
import type { LobbyMessage } from '@nhex/shared'
import { useAuthStore } from '@/stores/auth'
import type { SiteRole } from '@/services/rolePresentation'
import { isOnlineAccount, normalizePresenceCount } from '@/services/socialPresentation'

const CHAT_POLL_INTERVAL_MS = 2_000
const TABLES_POLL_INTERVAL_MS = 10_000
const ONLINE_POLL_INTERVAL_MS = 5_000
const HEARTBEAT_INTERVAL_MS = 4_000
const FRIENDS_POLL_INTERVAL_MS = 15_000
const CATALOG_POLL_INTERVAL_MS = 30_000
const TABLES_RECOVERY_POLL_INTERVAL_MS = 30_000
const ONLINE_RECOVERY_POLL_INTERVAL_MS = 15_000

function envFlag(name: string, fallback: boolean): boolean {
  const raw = String((import.meta.env as Record<string, unknown>)[name] ?? '').trim().toLowerCase()
  if (!raw) return fallback
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on'
}

// Push is the normal path. These switches keep rollout reversible while
// polling remains available as a recovery path.
const LOBBY_REALTIME_ENABLED = envFlag('VITE_REALTIME_LOBBY_ENABLED', true)
const CHAT_REALTIME_ENABLED = envFlag('VITE_REALTIME_CHAT_ENABLED', true)
const POLLING_FALLBACK_ENABLED = envFlag('VITE_POLLING_FALLBACK_ENABLED', true)

function delay(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

function isTransientRequestError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status
  return status === undefined || status >= 500
}

/**
 * Store lobby — stoły, gracze online, globalny czat, heartbeat.
 * Odwzorowuje funkcjonalność oryginalnego index.html (4001 linii).
 */

/* ==================== TYPY ==================== */

export interface OnlineUser {
  id: string
  username: string
  role: SiteRole
  is_friend: boolean
  is_staff: boolean
  activity: PresenceActivity
  current_table?: string | null
  connected_at?: string
  has_lobby: boolean
  has_table: boolean
  nickname_censored: boolean
  vanished: boolean
	muted: boolean
	banned: boolean
}

export type PresenceActivity = 'available' | 'table' | 'armies' | 'footer' | 'admin' | 'unavailable'

export interface PresenceState {
  activity: PresenceActivity
  tableId?: string | null
}

export interface TableInfo {
  id: string
  name: string
  owner_name: string
  owner_id: string
  owner_role: SiteRole
  board_id: string
  has_password: boolean
  visibility: 'public' | 'private'
  game_mode: '1v1' | '2v2'
  allow_friends: boolean
  player_count: number
  spectator_count: number
  max_players: number
  created_at: string
  seats: TableSeat[]
  spectator_link?: string
  is_whitelisted?: boolean
  password_verified?: boolean
}

export interface TableSeat {
  index: number
  player_name: string | null
  role: SiteRole
  link_invitation?: string
}

export interface GlobalChatMessage {
  id: string
  user_id: string
  username: string
  message: string
  timestamp: string
  role?: SiteRole
	nickname_censored?: boolean
	muted?: boolean
	banned?: boolean
}

export interface GlobalChatPolicyMessage {
  enabled: boolean
}

export interface FriendRequest {
  id: string
  from_user_id: string
  from_username: string
  created_at: string
}

export interface UserNotice {
  id: number
  title: string
  message: string
  created_at: string
}

export interface ServerInfo {
  tss_ws_url: string
  armies: ArmyMeta[]
  boards: BoardMeta[]
  emotes: EmoteMeta[]
  links: ContentLink[]
  footer_links: FooterLink[]
  keymap: KeymapEntry[]
}

export interface KeymapEntry {
  id: string
  type?: 'shortcut' | 'section'
  key?: string
  label_pl?: string
  label_en?: string
  title_pl?: string
  title_en?: string
}

export interface ContentLink {
  id: string
  name: string
  name_pl: string
  name_eng: string
  url_pl: string
  url_en: string
  source_pl?: 'url' | 'pdf' | ''
  source_en?: 'url' | 'pdf' | ''
  pdf_pl?: string
  pdf_en?: string
}

export interface FooterLink {
  id: string
  name: string
  name_pl: string
  name_eng: string
  title: string
  url: string
  system_key?: 'tutorial' | 'terms' | 'support'
}

export interface ArmyMeta {
  id: string
  name: string
  name_pl?: string
  name_eng?: string
  icon_url?: string
  owner_id?: string
  custom?: boolean
  private?: boolean
  readonly?: boolean
  balanced?: boolean
  utility?: boolean
  official?: boolean
  is_own?: boolean
  menu_group?: 'official' | 'fan' | 'private' | 'balanced' | 'extras' | 'utility'
  spawner_image?: string
  key_shortcut?: string
  primary_color?: string
  secondary_color?: string
}

export interface BoardMeta {
  id: string
  name: string
  image_url: string
}

export interface EmoteMeta {
  id: string
  name: string
  image_url: string
  images?: string[]
  key_shortcut?: string
  cursor_offset_x?: number
  cursor_offset_y?: number
}

export const useLobbyStore = defineStore('lobby', () => {
  const API_BASE = import.meta.env.VITE_API_URL || '/api'
  const auth = useAuthStore()

  /* ==================== STAN ==================== */
  const tables = ref<TableInfo[]>([])
  const onlineUsers = ref<OnlineUser[]>([])
	const publicOnlineCount = ref(0)
  const guestCount = ref(0)
  const chatMessages = ref<GlobalChatMessage[]>([])
  const globalChatEnabled = ref(true)
  const globalChatStatusRevision = ref(0)
  const friendRequests = ref<FriendRequest[]>([])
  const notices = ref<UserNotice[]>([])
  const serverInfo = ref<ServerInfo | null>(null)
  const loading = ref(false)
  const tablesReady = ref(false)
  const tablesError = ref('')
  const chatLoading = ref(false)
  const onlineLoading = ref(false)
  const onlineUpdatedAt = ref<string | null>(null)
	const anonymousVisitorID = (() => {
		const storageKey = 'nhex.publicVisitor.v1'
		try {
			const existing = localStorage.getItem(storageKey)?.trim()
			if (existing) return existing
			const created = crypto.randomUUID()
			localStorage.setItem(storageKey, created)
			return created
		} catch {
			return crypto.randomUUID()
		}
	})()

  /* Interwały pollingu */
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null
  let chatPollInterval: ReturnType<typeof setInterval> | null = null
  let chatEventSource: EventSource | null = null
  let tablesPollInterval: ReturnType<typeof setInterval> | null = null
  let onlinePollInterval: ReturnType<typeof setInterval> | null = null
  let friendsPollInterval: ReturnType<typeof setInterval> | null = null
  let catalogPollInterval: ReturnType<typeof setInterval> | null = null
  let lobbySocket: WebSocket | null = null
  let lobbyReconnectTimer: ReturnType<typeof setTimeout> | null = null
  let lobbyRealtimeEnabled = false
  let lobbySocketConnected = false
  let lobbyReconnectAttempt = 0
  let chatStreamConnected = false
  let lifecycleGeneration = 0
  let presenceState: PresenceState = { activity: 'unavailable', tableId: null }
  let heartbeatInFlight = false
  let heartbeatRefreshPending = false
  let heartbeatAbortController: AbortController | null = null
  let presenceListenersAttached = false
  const presenceTabID = crypto.randomUUID()
  let presenceActivationVersion = 1
  let sentPresenceActivationVersion = 0

  /* Guardy przed nakładaniem requestów przy wolniejszej sieci/API */
  let tablesFetchInFlight = false
  let tablesRefreshPending = false
  let onlineFetchInFlight = false
  let onlineRefreshPending = false
  let chatFetchInFlight = false
  let friendsFetchInFlight = false
  let noticesFetchInFlight = false
  let serverInfoFetchPromise: Promise<void> | null = null
  let serverInfoRefreshPending = false
  let serverInfoReady = false

  function hasAuthToken(): boolean {
    return auth.isAuthenticated && Boolean(getAccessToken())
  }

  function stopLobbyServices(): void {
    stopChatRealtime()
    stopChatPolling()
    stopTablesPolling()
    stopOnlinePolling()
    stopFriendsPolling()
    stopCatalogRefresh()
    stopLobbyRealtime()
  }

  function stopAllPolling(): void {
    stopHeartbeat()
    stopLobbyServices()
  }

  function handleUnauthorized(error: unknown): boolean {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status === 401) {
      lifecycleGeneration += 1
      stopAllPolling()
      auth.invalidateSession()
      return true
    }
    return false
  }

  function normalizeTssUrl(rawUrl: string): string {
    let url = rawUrl.trim()
    if (!url) return url

    if (url.startsWith('/')) {
      if (typeof window === 'undefined') return url
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
      return `${protocol}://${window.location.host}${url}`
    }

    if (url.startsWith('http://')) {
      url = `ws://${url.slice('http://'.length)}`
    } else if (url.startsWith('https://')) {
      url = `wss://${url.slice('https://'.length)}`
    }

    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('ws://')) {
      url = `wss://${url.slice('ws://'.length)}`
    }

    return url
  }

  function normalizeChatMessage(message: GlobalChatMessage): GlobalChatMessage {
    return {
      ...message,
      id: String(message.id),
      user_id: message.user_id ? String(message.user_id) : '',
      timestamp: message.timestamp ?? '',
    }
  }

  function appendChatMessages(messages: GlobalChatMessage[]): void {
    if (messages.length === 0) return
    const existingIds = new Set(chatMessages.value.map(m => m.id))
    const newMsgs = messages
      .map(normalizeChatMessage)
      .filter(m => !existingIds.has(m.id))
    if (newMsgs.length === 0) return
    chatMessages.value.push(...newMsgs)
  }

  /* ==================== GETTERY ==================== */
  const onlineCount = computed(() => Math.max(publicOnlineCount.value, onlineUsers.value.length))
  const tssUrl = computed(() => normalizeTssUrl(serverInfo.value?.tss_ws_url ?? 'ws://localhost:3001'))
  const hasPendingRequests = computed(() => friendRequests.value.length > 0)

  function getLobbySocketUrl(): string {
    const parsed = new URL(tssUrl.value, window.location.origin)
    parsed.pathname = '/lobby-ws'
    parsed.search = ''
    const token = getAccessToken()
    if (token) parsed.searchParams.set('token', token)
    return parsed.toString()
  }

  function retireLobbySocket(socket: WebSocket): void {
    socket.onmessage = null
    socket.onclose = null
    socket.onerror = null
    if (socket.readyState === WebSocket.CONNECTING) {
      socket.onopen = () => socket.close(1000, 'Lobby view closed')
    } else if (socket.readyState === WebSocket.OPEN) {
      socket.onopen = null
      socket.close(1000, 'Lobby view closed')
    } else {
      socket.onopen = null
    }
  }

  function startLobbyRealtime(): void {
    if (!LOBBY_REALTIME_ENABLED) return
    lobbyRealtimeEnabled = true
    if (!hasAuthToken() || !serverInfo.value || lobbySocket) return

    try {
      const socket = new WebSocket(getLobbySocketUrl())
      lobbySocket = socket
      socket.onopen = () => {
        if (lobbySocket !== socket || !lobbyRealtimeEnabled) {
          retireLobbySocket(socket)
          return
        }
        lobbySocketConnected = true
        lobbyReconnectAttempt = 0
        stopTablesPolling()
        stopOnlinePolling()
        void fetchOnlineUsers()
        void fetchServerInfo()
        socket.send(JSON.stringify({
          subscribe: 'lobby',
          userId: auth.userId,
          userName: auth.username,
          accountId: auth.userId,
        }))
      }
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as LobbyMessage
          if (message.removeTable) {
            tables.value = tables.value.filter(table => table.id !== message.removeTable!.id)
          }
          if (message.tables || message.addTable || message.updateTable ||
              message.addPlayer || message.removePlayer || message.playerOffline || message.playerOnline) {
            void fetchTables()
          }
          if (message.onlineUsers || message.addOnlineUser || message.updateOnlineUser || message.removeOnlineUser) {
            // TSS informs us that presence changed. The backend heartbeat record
            // remains authoritative, especially when one account has many tabs.
            void fetchOnlineUsers()
          }
          if (message.globalChat) {
            appendChatMessages(message.globalChat.map(item => ({
              id: String(item.id),
              user_id: '',
              username: item.username,
              message: item.message,
              timestamp: '',
              role: item.role as SiteRole | undefined,
            })))
          }
          if (message.globalChatReset) {
            chatMessages.value = []
            void fetchChat()
          }
          if (message.globalChatDeleted) {
            const deletedId = String(message.globalChatDeleted.id)
            chatMessages.value = chatMessages.value.filter(item => item.id !== deletedId)
          }
          if (message.armyCatalogChanged || message.serverInfoChanged) {
            void fetchServerInfo()
          }
        } catch { /* ignore malformed realtime event */ }
      }
      socket.onclose = () => {
        if (lobbySocket === socket) lobbySocket = null
        lobbySocketConnected = false
        if (lobbyRealtimeEnabled && hasAuthToken()) {
          if (POLLING_FALLBACK_ENABLED) {
            startTablesPolling()
            startOnlinePolling()
          }
          const baseDelay = Math.min(30_000, 1_000 * 2 ** Math.min(lobbyReconnectAttempt, 5))
          lobbyReconnectAttempt += 1
          lobbyReconnectTimer = setTimeout(
            startLobbyRealtime,
            Math.round(baseDelay * (0.8 + Math.random() * 0.4)),
          )
        }
      }
      socket.onerror = () => {
        if (lobbySocket === socket && socket.readyState === WebSocket.OPEN) socket.close()
      }
    } catch {
      lobbySocket = null
      lobbySocketConnected = false
      if (lobbyRealtimeEnabled) {
        if (POLLING_FALLBACK_ENABLED) {
          startTablesPolling()
          startOnlinePolling()
        }
        const baseDelay = Math.min(30_000, 1_000 * 2 ** Math.min(lobbyReconnectAttempt, 5))
        lobbyReconnectAttempt += 1
        lobbyReconnectTimer = setTimeout(
          startLobbyRealtime,
          Math.round(baseDelay * (0.8 + Math.random() * 0.4)),
        )
      }
    }
  }

  function stopLobbyRealtime(): void {
    lobbyRealtimeEnabled = false
    lobbySocketConnected = false
    lobbyReconnectAttempt = 0
    if (lobbyReconnectTimer) {
      clearTimeout(lobbyReconnectTimer)
      lobbyReconnectTimer = null
    }
    if (lobbySocket) {
      const socket = lobbySocket
      lobbySocket = null
      retireLobbySocket(socket)
    }
  }

  function isTablePasswordVerified(tableId: string): boolean {
    return tables.value.find(table => table.id === tableId)?.password_verified === true
  }

  /* ==================== STOŁY ==================== */

  /** Pobierz listę stołów (z metadanymi od TSS) */
  async function fetchTables(): Promise<void> {
    if (tablesFetchInFlight) {
      tablesRefreshPending = true
      return
    }
    tablesFetchInFlight = true
    loading.value = true
    try {
      interface RawChair {
        id: string
        name: string | null
        kind: 'p' | 's'
        role: string
        site_role?: SiteRole
        link_invitation: string | null
      }
      interface RawTable {
        id: string
        name: string
        owner_id: string
        owner_name: string
        owner_role?: SiteRole
        board_id: string
        visibility: 'public' | 'private'
        game_mode: '1v1' | '2v2'
        max_players: number
        has_password: boolean
        allow_friends: boolean
        is_whitelisted?: boolean
        password_verified?: boolean
        spectator_count?: number
        created_at: string
        updated_at: string
        chairs: RawChair[]
      }
      const { data } = await api.get<RawTable[]>('/tables')
      tables.value = data.map((raw) => {
        const playerChairs = raw.chairs.filter(ch => ch.kind === 'p')
        const spectatorChairs = raw.chairs.filter(ch => ch.kind === 's')
        return {
          id: raw.id,
          name: raw.name,
          owner_id: raw.owner_id,
          owner_name: raw.owner_name ?? '',
          owner_role: raw.owner_role ?? 'player',
          board_id: raw.board_id,
          visibility: raw.visibility,
          game_mode: raw.game_mode,
          max_players: raw.max_players,
          has_password: raw.has_password,
          allow_friends: raw.allow_friends,
          is_whitelisted: raw.is_whitelisted ?? false,
          password_verified: raw.password_verified === true,
          created_at: raw.created_at,
          player_count: playerChairs.filter(ch => ch.name).length,
          spectator_count: raw.spectator_count ?? spectatorChairs.filter(ch => ch.name).length,
          seats: playerChairs.map((ch, idx) => ({
            index: idx,
            player_name: ch.name,
            role: ch.site_role ?? 'player',
            link_invitation: ch.link_invitation ?? undefined,
          })),
          spectator_link: spectatorChairs[0]?.link_invitation ?? undefined,
        } as TableInfo
      })
      tablesError.value = ''
      tablesReady.value = true
    } catch (error) {
      if (handleUnauthorized(error)) return
      if (!tablesReady.value) {
        tablesError.value = 'Tables are temporarily unavailable'
        tablesReady.value = true
      }
    } finally {
      loading.value = false
      tablesFetchInFlight = false
      if (tablesRefreshPending) {
        tablesRefreshPending = false
        queueMicrotask(() => void fetchTables())
      }
    }
  }

  /** Utwórz nowy stół */
  async function createTable(payload: {
    name: string
    board_id: string
    game_mode: string
    visibility: string
    password?: string
    whitelist?: string[]
    allow_friends?: boolean
  }): Promise<string> {
    const { data } = await api.post<{ id: string }>('/tables', payload)
    await fetchTables()
    return data.id
  }

  /** Zaktualizuj stół */
  async function updateTable(tableId: string, payload: {
    name?: string
    board_id?: string
    game_mode?: string
    visibility?: string
    password?: string
    whitelist?: string[]
    allow_friends?: boolean
  }): Promise<void> {
    await api.put(`/tables/${tableId}`, payload)
    await fetchTables()
  }

  /** Usuń stół */
  async function deleteTable(tableId: string): Promise<void> {
    await api.delete(`/tables/${tableId}`)
    tables.value = tables.value.filter((t) => t.id !== tableId)
  }

  /** Weryfikuj hasło stołu */
  async function verifyTablePassword(
    tableId: string,
    password: string,
  ): Promise<boolean> {
    try {
      await api.post(`/tables/${tableId}/verify-password`, { password })
      await fetchTables()
      return true
    } catch {
      return false
    }
  }

  /* ==================== GRACZE ONLINE ==================== */

  async function fetchOnlineUsers(): Promise<void> {
    if (onlineFetchInFlight) {
      onlineRefreshPending = true
      return
    }
    onlineFetchInFlight = true
    if (onlineUpdatedAt.value === null) onlineLoading.value = true
    try {
      do {
        onlineRefreshPending = false
        try {
          const { data } = await api.get<{ count: number; guest_count?: number; users: Array<{
            user_id: string
            username: string
            role: string
            activity?: PresenceActivity
            last_seen: string
            connected_at?: string
            current_table_id?: string
            is_friend?: boolean
            has_lobby?: boolean
            has_table?: boolean
            nickname_censored?: boolean
            vanished?: boolean
			muted?: boolean
			banned?: boolean
          }> }>('/lobby/online')
          const users = Array.isArray(data.users) ? data.users.filter(isOnlineAccount) : []
          publicOnlineCount.value = normalizePresenceCount(data.count, users.length)
          guestCount.value = normalizePresenceCount(data.guest_count)
          onlineUsers.value = users.map(u => ({
            id: u.user_id,
            username: u.username,
            role: (u.role || 'player') as SiteRole,
            is_friend: u.is_friend === true,
            is_staff: u.role === 'admin' || u.role === 'moderator',
            activity: normalizePresenceActivity(u.activity, u.current_table_id),
            current_table: u.current_table_id ?? null,
            connected_at: u.connected_at,
            has_lobby: u.has_lobby === true,
            has_table: u.has_table === true,
            nickname_censored: u.nickname_censored === true,
            vanished: u.vanished === true,
			muted: u.muted === true,
			banned: u.banned === true,
          }))
          onlineUpdatedAt.value = new Date().toISOString()
        } catch (error) {
          if (handleUnauthorized(error)) return
          /* Keep the last authoritative list through transient failures. */
        }
      } while (onlineRefreshPending)
    } finally {
      onlineFetchInFlight = false
      onlineLoading.value = false
    }
  }

  function normalizePresenceActivity(activity: unknown, tableId?: string | null): PresenceActivity {
    if (activity === 'available' || activity === 'armies' || activity === 'footer' || activity === 'admin' || activity === 'unavailable') return activity
    if (activity === 'table') return tableId ? 'table' : 'unavailable'
    return tableId ? 'table' : 'available'
  }

  function normalizedPresence(next: PresenceState): PresenceState {
    const tableId = next.tableId?.trim() || null
    const activity = normalizePresenceActivity(next.activity, tableId)
    return {
      activity,
      tableId: activity === 'table' ? tableId : null,
    }
  }

  function canActivatePresence(): boolean {
    return typeof document === 'undefined' || document.visibilityState === 'visible'
  }

  function activatePresence(): void {
    if (!canActivatePresence()) return
    presenceActivationVersion += 1
    void sendHeartbeat(true)
  }

  function onPresenceFocus(): void {
    activatePresence()
  }

  function onPresenceVisibilityChange(): void {
    if (document.visibilityState === 'visible') activatePresence()
  }

  function onPresencePageHide(): void {
    const token = getAccessToken()
	if (!token) {
		void fetch(`${API_BASE}/lobby/heartbeat`, {
			method: 'POST', keepalive: true,
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ visitor_id: anonymousVisitorID, offline: true }),
		}).catch(() => undefined)
		return
	}
    void fetch(`${API_BASE}/lobby/offline`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ tab_id: presenceTabID }),
    }).catch(() => undefined)
  }

  function attachPresenceListeners(): void {
    if (presenceListenersAttached) return
    presenceListenersAttached = true
    window.addEventListener('focus', onPresenceFocus)
    window.addEventListener('pageshow', onPresenceFocus)
    window.addEventListener('pagehide', onPresencePageHide)
    document.addEventListener('visibilitychange', onPresenceVisibilityChange)
  }

  function detachPresenceListeners(): void {
    if (!presenceListenersAttached) return
    presenceListenersAttached = false
    window.removeEventListener('focus', onPresenceFocus)
    window.removeEventListener('pageshow', onPresenceFocus)
    window.removeEventListener('pagehide', onPresencePageHide)
    document.removeEventListener('visibilitychange', onPresenceVisibilityChange)
  }

  /** Heartbeat is deliberately much shorter than the server presence lease. */
  function startHeartbeat(next: PresenceState = presenceState): void {
    presenceState = normalizedPresence(next)
    attachPresenceListeners()
    if (!heartbeatInterval) {
      heartbeatInterval = setInterval(() => void sendHeartbeat(), HEARTBEAT_INTERVAL_MS)
    }
    if (canActivatePresence()) activatePresence()
  }

  function stopHeartbeat(): void {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
    heartbeatRefreshPending = false
    heartbeatAbortController?.abort()
    heartbeatAbortController = null
    detachPresenceListeners()
  }

  async function sendHeartbeat(_force = false): Promise<boolean> {
    if (heartbeatInFlight) {
      heartbeatRefreshPending = true
      return true
    }
    heartbeatInFlight = true
    heartbeatRefreshPending = false
    const sentPresence = { ...presenceState }
    const activationVersion = presenceActivationVersion
    const claimsActive = activationVersion > sentPresenceActivationVersion
    const controller = new AbortController()
    heartbeatAbortController = controller
    try {
      const payload = {
        activity: sentPresence.activity,
        table_id: sentPresence.tableId ?? null,
        tab_id: presenceTabID,
        active: claimsActive,
		visitor_id: anonymousVisitorID,
      }
      if (hasAuthToken()) {
        await api.post('/lobby/heartbeat', payload, { signal: controller.signal })
      } else {
        const response = await fetch(`${API_BASE}/lobby/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitor_id: anonymousVisitorID }),
          signal: controller.signal,
        })
        if (!response.ok) throw new Error(`Anonymous heartbeat failed (${response.status})`)
      }
      if (claimsActive) sentPresenceActivationVersion = Math.max(sentPresenceActivationVersion, activationVersion)
      return true
    } catch (error) {
      if (controller.signal.aborted) return false
      if (handleUnauthorized(error)) return false
      return false
    } finally {
      heartbeatInFlight = false
      if (heartbeatAbortController === controller) heartbeatAbortController = null
      if (heartbeatRefreshPending) {
        heartbeatRefreshPending = false
        queueMicrotask(() => void sendHeartbeat())
      }
    }
  }

  /* ==================== CZAT GLOBALNY ==================== */

  async function fetchChat(): Promise<void> {
    if (chatFetchInFlight) return
    chatFetchInFlight = true
    try {
      const { data } = await api.get<GlobalChatMessage[]>('/chat/messages')
      chatMessages.value = data.map(normalizeChatMessage)
    } catch (error) {
      if (handleUnauthorized(error)) return
      /* cicho */
    }
    finally {
      chatFetchInFlight = false
    }
  }

  async function sendChatMessage(content: string): Promise<void> {
    chatLoading.value = true
    try {
      await api.post('/chat/messages', { message: content })
      await fetchChat()
    } finally {
      chatLoading.value = false
    }
  }

  async function deleteGlobalChatMessage(id: string): Promise<void> {
    const normalized = String(id).trim()
    if (!/^\d+$/.test(normalized)) throw new Error('Invalid message id')
    await api.delete(`/mod/chat/${normalized}`)
    chatMessages.value = chatMessages.value.filter(message => message.id !== normalized)
  }

  /** Polling czatu co 3s (fallback gdy SSE jest niedostępne) */
  function startChatPolling(): void {
    if (chatStreamConnected || !POLLING_FALLBACK_ENABLED) return
    if (chatPollInterval) return
    stopChatPolling()
    fetchChat()
    chatPollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchChat()
    }, CHAT_POLL_INTERVAL_MS)
  }

  function stopChatPolling(): void {
    if (chatPollInterval) {
      clearInterval(chatPollInterval)
      chatPollInterval = null
    }
  }

  /** Realtime chat przez SSE, z fallbackiem do pollingu. */
  function startChatRealtime(): void {
    stopChatRealtime()
    stopChatPolling()

    if (!CHAT_REALTIME_ENABLED || typeof window === 'undefined' || typeof EventSource === 'undefined') {
      startChatPolling()
      return
    }

    if (chatMessages.value.length === 0) {
      fetchChat()
    }
    const normalizedBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE
    const streamUrl = `${normalizedBase}/chat/stream`
    const allowedStreamOrigin = (() => {
      try {
        return new URL(streamUrl, window.location.origin).origin
      } catch {
        return window.location.origin
      }
    })()
    chatEventSource = new EventSource(streamUrl, { withCredentials: true })
    chatEventSource.onopen = () => {
      chatStreamConnected = true
      stopChatPolling()
    }

    chatEventSource.addEventListener('message', (event: MessageEvent) => {
      const eventOrigin = typeof event.origin === 'string' ? event.origin : ''
      if (eventOrigin && eventOrigin !== allowedStreamOrigin) {
        return
      }
      try {
        const payload = JSON.parse(event.data) as GlobalChatMessage
        appendChatMessages([payload])
      } catch {
        // Ignorujemy pojedyncze błędne eventy.
      }
    })

    chatEventSource.addEventListener('policy', (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as GlobalChatPolicyMessage
        globalChatEnabled.value = payload.enabled !== false
      } catch {
        // Ignorujemy pojedyncze błędne eventy.
      }
    })

    chatEventSource.addEventListener('status', (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as { user_id?: string }
        if (payload.user_id === auth.userId) globalChatStatusRevision.value += 1
        void fetchOnlineUsers()
		if (hasAuthToken()) void Promise.all([fetchFriendRequests(), fetchNotices()])
		window.dispatchEvent(new CustomEvent('nhex:social-changed', { detail: payload }))
      } catch {
        // Ignore one malformed status event and keep the stream alive.
      }
    })

	chatEventSource.addEventListener('army-catalog', () => {
		void fetchServerInfo()
		window.dispatchEvent(new CustomEvent('nhex:army-catalog-changed'))
	})

    chatEventSource.addEventListener('reset', () => {
      chatMessages.value = []
      void fetchChat()
    })

    chatEventSource.onerror = () => {
      // Przy błędach transportu przechodzimy na sprawdzony polling.
      chatStreamConnected = false
      startChatPolling()
    }
  }

  function stopChatRealtime(): void {
    chatStreamConnected = false
    if (chatEventSource) {
      chatEventSource.close()
      chatEventSource = null
    }
  }

  /** Polling stołów co 8s */
  function startTablesPolling(refreshNow = true): void {
    if (lobbySocketConnected) return
    stopTablesPolling()
    if (refreshNow || !tablesReady.value) void fetchTables()
    const interval = LOBBY_REALTIME_ENABLED && hasAuthToken()
      ? TABLES_RECOVERY_POLL_INTERVAL_MS
      : TABLES_POLL_INTERVAL_MS
    tablesPollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchTables()
    }, interval)
  }

  function stopTablesPolling(): void {
    if (tablesPollInterval) {
      clearInterval(tablesPollInterval)
      tablesPollInterval = null
    }
  }

  /** Realtime events lead; this short poll is the recovery path after a missed event. */
  function startOnlinePolling(refreshNow = true): void {
    if (lobbySocketConnected) return
    stopOnlinePolling()
    if (refreshNow || onlineUpdatedAt.value === null) void fetchOnlineUsers()
    const interval = LOBBY_REALTIME_ENABLED && hasAuthToken()
      ? ONLINE_RECOVERY_POLL_INTERVAL_MS
      : ONLINE_POLL_INTERVAL_MS
    onlinePollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchOnlineUsers()
    }, interval)
  }

  function stopOnlinePolling(): void {
    if (onlinePollInterval) {
      clearInterval(onlinePollInterval)
      onlinePollInterval = null
    }
  }

  /** Polling friend requests co 20s */
  function startFriendsPolling(): void {
    if (!hasAuthToken()) return
    stopFriendsPolling()
    void Promise.all([fetchFriendRequests(), fetchNotices()])
    friendsPollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      void Promise.all([fetchFriendRequests(), fetchNotices()])
    }, FRIENDS_POLL_INTERVAL_MS)
  }

  function stopFriendsPolling(): void {
    if (friendsPollInterval) {
      clearInterval(friendsPollInterval)
      friendsPollInterval = null
    }
  }

  /* ==================== ZNAJOMI ==================== */

  async function fetchFriendRequests(): Promise<void> {
    if (!hasAuthToken()) {
      stopFriendsPolling()
      return
    }
    if (friendsFetchInFlight) return
    friendsFetchInFlight = true
    try {
      const { data } = await api.get<Array<{
        id: string
        from_user: { id: string; username: string }
        created_at: string
      }>>('/social/friends/requests')
      friendRequests.value = (Array.isArray(data) ? data : [])
        .filter(r => r && r.from_user && typeof r.from_user.id === 'string' && r.from_user.id.trim() &&
          typeof r.from_user.username === 'string' && r.from_user.username.trim())
        .map(r => ({
        id: r.id,
        from_user_id: r.from_user.id,
        from_username: r.from_user.username,
        created_at: r.created_at,
      }))
    } catch (error) {
      if (handleUnauthorized(error)) return
      /* cicho */
    }
    finally {
      friendsFetchInFlight = false
    }
  }

  async function leaveAuthenticatedPresence(): Promise<void> {
    stopHeartbeat()
    if (!hasAuthToken()) return
    try {
      await api.post('/lobby/offline', { tab_id: presenceTabID })
    } catch (error) {
      if (!handleUnauthorized(error)) {
        // Logout must continue even when the presence endpoint is unavailable.
      }
    }
  }

  async function fetchNotices(): Promise<void> {
    if (!hasAuthToken() || noticesFetchInFlight) return
    noticesFetchInFlight = true
    try {
      const { data } = await api.get<UserNotice[]>('/social/notices')
      notices.value = Array.isArray(data) ? data : []
    } catch (error) {
      if (handleUnauthorized(error)) return
    } finally {
      noticesFetchInFlight = false
    }
  }

  async function acknowledgeNotice(id: number): Promise<void> {
    await api.delete(`/social/notices/${id}`)
    notices.value = notices.value.filter(notice => notice.id !== id)
  }

  async function addFriend(username: string): Promise<void> {
    await api.post('/social/friends/request', { username })
    await Promise.all([
      fetchFriendRequests(),
      fetchOnlineUsers(),
    ])
  }

  async function acceptFriendRequest(requestId: string): Promise<void> {
    await api.post(`/social/friends/requests/${requestId}/accept`)
    friendRequests.value = friendRequests.value.filter((r) => r.id !== requestId)
    await fetchOnlineUsers()
  }

  async function rejectFriendRequest(requestId: string): Promise<void> {
    await api.delete(`/social/friends/requests/${requestId}`)
    friendRequests.value = friendRequests.value.filter((r) => r.id !== requestId)
  }

  async function removeFriend(friendId: string): Promise<void> {
    await api.delete(`/social/friends/${encodeURIComponent(friendId)}`)
    const normalized = friendId.trim().toLowerCase()
    onlineUsers.value = onlineUsers.value.map(user =>
      user.id === friendId || user.username.toLowerCase() === normalized
        ? { ...user, is_friend: false }
        : user,
    )
  }

  /* ==================== MODERACJA ==================== */

  async function reportUser(
    userId: string,
    reason: string,
  ): Promise<void> {
    await api.post('/social/reports', { user_id: userId, reason })
  }

  /** POST /api/mod/mute — expects { username, mute_type: 'all'|'global', duration?: '30m' } */
  async function muteUser(
    username: string,
    muteType: 'all' | 'global',
    duration: string,
	userId = '',
  ): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>('/mod/mute', {
      username,
	  user_id: userId || undefined,
      mute_type: muteType,
      duration: duration || undefined,
    })
    return data
  }

  /** DELETE /api/mod/mute/:userId — unmute */
  async function unmuteUser(userId: string): Promise<{ message: string; removed: number }> {
    const { data } = await api.delete<{ message: string; removed: number }>(`/mod/mute/${userId}`)
    return data
  }

  /** POST /api/mod/ban — expects { username, reason, duration?: '2h' } */
  async function banUser(
    username: string,
    reason: string,
    duration: string,
	userId = '',
  ): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>('/mod/ban', {
      username,
	  user_id: userId || undefined,
      reason,
      duration: duration || undefined,
    })
    return data
  }

  /** DELETE /api/mod/ban/:userId — unban */
  async function unbanUser(userId: string): Promise<{ message: string }> {
    const { data } = await api.delete<{ message: string }>(`/mod/ban/${userId}`)
    return data
  }

  /* ==================== INFO SERWERA ==================== */

  async function fetchServerInfo(): Promise<void> {
    if (serverInfoFetchPromise) {
      serverInfoRefreshPending = true
      return serverInfoFetchPromise
    }

    serverInfoFetchPromise = (async () => {
      do {
        serverInfoRefreshPending = false
        for (let attempt = 0; attempt < 15; attempt += 1) {
          try {
            const { data } = await api.get<ServerInfo>('/lobby/server-info')
            serverInfo.value = data
            serverInfoReady = true
            break
          } catch (error) {
            if (handleUnauthorized(error)) return
            if (!isTransientRequestError(error) || attempt === 14) break
            await delay(Math.min(250 * (attempt + 1), 1_000))
          }
        }
      } while (serverInfoRefreshPending)
    })().finally(() => {
      serverInfoFetchPromise = null
    })

    return serverInfoFetchPromise
  }

  function refreshCatalogOnFocus(): void {
    if (!document.hidden) void fetchServerInfo()
  }

  function startCatalogRefresh(): void {
    stopCatalogRefresh()
    catalogPollInterval = setInterval(() => void fetchServerInfo(), CATALOG_POLL_INTERVAL_MS)
    window.addEventListener('focus', refreshCatalogOnFocus)
  }

  function stopCatalogRefresh(): void {
    if (catalogPollInterval) {
      clearInterval(catalogPollInterval)
      catalogPollInterval = null
    }
    window.removeEventListener('focus', refreshCatalogOnFocus)
  }

  /* ==================== LIFECYCLE ==================== */

  /** Inicjalizuj lobby — pobierz dane i uruchom polling */
  async function initLobby(): Promise<void> {
    const generation = ++lifecycleGeneration
    stopLobbyServices()
    await fetchServerInfo()
    if (generation !== lifecycleGeneration) return
    if (!serverInfoReady) {
      await delay(2_000)
      if (generation === lifecycleGeneration) void initLobby()
      return
    }
    const initialRequests: Promise<unknown>[] = [
      fetchTables(),
      fetchOnlineUsers(),
      fetchChat(),
    ]
    if (hasAuthToken()) initialRequests.push(fetchFriendRequests(), fetchNotices())
    await Promise.all(initialRequests)
    if (generation !== lifecycleGeneration) return
    startChatRealtime()
    if (POLLING_FALLBACK_ENABLED) {
      startTablesPolling(false)
      startOnlinePolling(false)
    }
    if (hasAuthToken()) {
      startFriendsPolling()
    }
    if (hasAuthToken() && LOBBY_REALTIME_ENABLED) {
      startLobbyRealtime()
    }
    startCatalogRefresh()
    startHeartbeat({ activity: 'available', tableId: null })
  }

  /** Zatrzymaj polling przy opuszczeniu lobby */
  function destroyLobby(): void {
    lifecycleGeneration += 1
	stopTablesPolling()
	stopOnlinePolling()
	stopFriendsPolling()
	stopCatalogRefresh()
	stopLobbyRealtime()
  }

  return {
    /* Stan */
    tables,
    onlineUsers,
    guestCount,
    chatMessages,
    globalChatEnabled,
    globalChatStatusRevision,
    friendRequests,
    notices,
    serverInfo,
    loading,
    tablesReady,
    tablesError,
    chatLoading,
    onlineLoading,
    onlineUpdatedAt,
    /* Gettery */
    onlineCount,
    tssUrl,
    hasPendingRequests,
    /* Akcje: stoły */
    fetchTables,
    createTable,
    updateTable,
    deleteTable,
    verifyTablePassword,
    isTablePasswordVerified,
    /* Akcje: online */
    fetchOnlineUsers,
    startHeartbeat,
    stopHeartbeat,
    leaveAuthenticatedPresence,
    /* Akcje: czat */
    fetchChat,
    sendChatMessage,
    deleteGlobalChatMessage,
    startChatRealtime,
    stopChatRealtime,
    startChatPolling,
    stopChatPolling,
    /* Akcje: znajomi */
    fetchFriendRequests,
    fetchNotices,
    acknowledgeNotice,
    addFriend,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    /* Akcje: moderacja */
    reportUser,
    muteUser,
    unmuteUser,
    banUser,
    unbanUser,
    /* Akcje: server info */
    fetchServerInfo,
    startLobbyRealtime,
    stopLobbyRealtime,
    /* Lifecycle */
    initLobby,
    destroyLobby,
  }
})
